import type React from "react";
import { ChevronRight } from "lucide-react";
import { useRoleContext } from "../../../../context/RolesContext";
import AddCustomRoleButton from "./AddCustomRoleButton";
import { useSelector } from "react-redux";
import type { RootState } from "../../../../store";

const RolesAside: React.FC = () => {
  const {
    defaultRoles,
    customRoles,
    isLoading,
    currentPage,
    activeRole,
    handleRoleClick,
  } = useRoleContext();

  const merchant = useSelector(
    (state: RootState) => state?.merchant?.permissions
  ) as string[] | undefined;

  const hasPermission = (permission: string): boolean => {
    return Array.isArray(merchant) && merchant.includes(permission);
  };

  const canAddRole = hasPermission("CAN_ADD_ROLE");
  const canAssignRole = hasPermission("CAN_ASSIGN_ROLE");

  if (isLoading || !defaultRoles.length) {
    return <div className="p-4 text-gray-500">Loading roles...</div>;
  }

  return (
    <div className="flex flex-col h-full">
      <div className="border-b px-4 py-3">
        <h2 className="font-medium text-gray-800 text-sm">Roles</h2>
      </div>

      <div className="flex-1 overflow-auto">
        {/* Default Roles Section */}
        <div className="px-4 py-2 bg-gray-50 text-xs font-semibold text-black uppercase tracking-wider">
          Default Roles
        </div>
        {defaultRoles.map((role) => (
          <div
            key={role.roleReference}
            className={`flex items-center px-4 py-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${
              activeRole === role.roleReference
                ? "bg-blue-50 border-l-4 border-l-blue-500"
                : "border-l-4 border-l-transparent"
            }`}
            onClick={() =>
              handleRoleClick(
                role.roleReference,
                role.rolePermissions,
                role.roleName
              )
            }
          >
            <span
              className={`flex-1 text-sm ${
                activeRole === role.roleReference
                  ? "text-blue-600 font-medium"
                  : "text-gray-600"
              }`}
            >
              {role.roleName}
            </span>
            {activeRole === role.roleReference && (
              <ChevronRight size={16} className="text-blue-500" />
            )}
          </div>
        ))}

        {/* Custom Roles Section */}
        <div className="px-4 py-2 bg-gray-50 text-xs font-semibold text-black uppercase tracking-wider">
          Custom Roles
        </div>
        {customRoles.length > 0 ? (
          customRoles.map((role) => (
            <div
              key={role.roleReference}
              className={`flex items-center px-4 py-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${
                activeRole === role.roleReference
                  ? "bg-blue-50 border-l-4 border-l-blue-500"
                  : "border-l-4 border-l-transparent"
              }`}
              onClick={() =>
                handleRoleClick(
                  role.roleReference,
                  role.rolePermissions,
                  role.roleName
                )
              }
            >
              <span
                className={`flex-1 text-sm ${
                  activeRole === role.roleReference
                    ? "text-blue-600 font-medium"
                    : "text-gray-600"
                }`}
              >
                {role.roleName}
              </span>
              {activeRole === role.roleReference && (
                <ChevronRight size={16} className="text-blue-500" />
              )}
            </div>
          ))
        ) : (
          <div className="px-4 py-3 text-sm text-gray-500 italic">
            No custom roles created
          </div>
        )}

        {isLoading && currentPage > 1 && (
          <div className="p-4 text-center text-gray-500 text-sm">
            Loading more...
          </div>
        )}
      </div>

      {/* Fixed conditional rendering */}
      {(canAddRole || canAssignRole) && (
        <div className="p-3 border-t mt-auto">
          <AddCustomRoleButton />
        </div>
      )}
    </div>
  );
};

export default RolesAside;
